import { useState, useEffect, useCallback } from "react";
import {
    Loader2, Save, FileText, Percent, CheckCircle2, AlertTriangle, ChevronDown,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import {
    REFERRAL_SETTINGS_URL, REFERRAL_TERMS_URL,
} from "../../constants/api";
import RichTextEditor from "../../components/common/RichTextEditor";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { formatDate } from "../../utils/datetime";

/** The referral programme's policy: what it pays, and what people agree to.
 *
 *  Both halves are tenant-wide, which is why they sit in Settings rather than
 *  on a customer. The rates only ever pre-fill a suggestion on the Referral
 *  Rewards screen — nothing is paid automatically, because what a set of
 *  referrals is worth is a commercial judgement and the three payout forms
 *  are not interchangeable.
 *
 *  Terms are append-only. A version somebody accepted can never be edited,
 *  because the acceptance record is evidence of the words they read.
 *  Publishing a new version stands the old one down and asks everyone again —
 *  which is the whole re-consent mechanism, and why publishing confirms twice.
 */
export default function ReferralProgramSettings() {
    const { fetch, update, create } = useApiMethods();

    const [orgPct, setOrgPct] = useState("");
    const [indPct, setIndPct] = useState("");
    const [savingRates, setSavingRates] = useState(false);

    const [versions, setVersions] = useState([]);
    const [loadingTerms, setLoadingTerms] = useState(true);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [publishing, setPublishing] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [openVersion, setOpenVersion] = useState(null);

    const loadRates = useCallback(() => {
        fetch(REFERRAL_SETTINGS_URL)
            .then(res => {
                if (res?.status === "success") {
                    const d = res.data || {};
                    setOrgPct(d.org_benefit_percent == null ? "" : String(d.org_benefit_percent));
                    setIndPct(d.individual_benefit_percent == null ? "" : String(d.individual_benefit_percent));
                }
            })
            .catch(() => ToastMsg("error", "Could not load referral rates"));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadTerms = useCallback(() => {
        setLoadingTerms(true);
        fetch(REFERRAL_TERMS_URL)
            .then(res => {
                if (res?.status === "success") setVersions(res.data || []);
                else ToastMsg("error", res?.message || "Could not load terms");
            })
            .catch(() => ToastMsg("error", "Could not load terms"))
            .finally(() => setLoadingTerms(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadRates(); loadTerms(); }, [loadRates, loadTerms]);

    const active = versions.find(v => v.is_active) || null;

    const saveRates = () => {
        setSavingRates(true);
        update(REFERRAL_SETTINGS_URL, {
            org_benefit_percent: orgPct === "" ? null : orgPct,
            individual_benefit_percent: indPct === "" ? null : indPct,
        })
            .then(res => {
                if (res?.status === "success") ToastMsg("success", res.message || "Saved");
                else ToastMsg("error", res?.message || "Could not save rates");
            })
            .catch(() => ToastMsg("error", "Could not save rates"))
            .finally(() => setSavingRates(false));
    };

    const publish = () => {
        setConfirming(false);
        setPublishing(true);
        create(REFERRAL_TERMS_URL, { title: title.trim(), body })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message || "Published");
                    setTitle("");
                    setBody("");
                    loadTerms();
                } else {
                    ToastMsg("error", res?.message || "Could not publish");
                }
            })
            .catch(() => ToastMsg("error", "Could not publish"))
            .finally(() => setPublishing(false));
    };

    // Quill leaves "<p><br></p>" behind when the box is cleared, which is not
    // content. The server collapses the same case; this keeps the button
    // honest before the round trip.
    const bodyHasText = body.replace(/<[^>]*>/g, "").trim().length > 0;
    const canPublish = title.trim() && bodyHasText && !publishing;

    return (
        <div className="p-5 space-y-5 max-w-5xl">
            <div>
                <h1 className="text-xl font-semibold text-gray-900">Referral Programme</h1>
                <p className="text-sm text-gray-500 mt-1">
                    The share rates rewards are suggested from, and the terms hospital
                    users accept before they can refer.
                </p>
            </div>

            {/* ── Rates ───────────────────────────────────────────────── */}
            <section className="rounded-lg border border-gray-200 bg-white">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                    <Percent className="w-4 h-4 text-gray-500" />
                    <h2 className="text-sm font-semibold text-gray-900">Share rates</h2>
                </div>
                <div className="p-4 space-y-4">
                    <p className="text-xs text-gray-500">
                        A percentage of what a referred hospital was won at. These only
                        pre-fill the figure when granting a reward — nothing pays out on
                        its own. Leave blank to quote no figure at all.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-xs font-medium text-gray-600">
                                Hospital rate (%)
                            </span>
                            <input
                                value={orgPct}
                                inputMode="decimal"
                                onChange={e => setOrgPct(e.target.value.replace(/[^\d.]/g, ""))}
                                placeholder="e.g. 5"
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                                    focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-medium text-gray-600">
                                Individual rate (%)
                            </span>
                            <input
                                value={indPct}
                                inputMode="decimal"
                                onChange={e => setIndPct(e.target.value.replace(/[^\d.]/g, ""))}
                                placeholder="e.g. 2"
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                                    focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </label>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={saveRates}
                            disabled={savingRates}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm
                                font-medium text-white bg-blue-600 hover:bg-blue-700
                                disabled:opacity-50"
                        >
                            {savingRates ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Save className="w-4 h-4" />}
                            Save rates
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Terms ───────────────────────────────────────────────── */}
            {!loadingTerms && !active && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3
                    text-sm text-amber-900 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                        No terms published. Until there are, hospital users can refer without
                        agreeing to anything — publish version 1 before enabling the programme
                        for any organisation.
                    </span>
                </div>
            )}

            <section className="rounded-lg border border-gray-200 bg-white">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <h2 className="text-sm font-semibold text-gray-900">
                        Publish a new version of the terms
                    </h2>
                </div>
                <div className="p-4 space-y-4">
                    <p className="text-xs text-gray-500">
                        Published versions cannot be edited — the acceptance record is
                        evidence of what somebody read. Publishing asks every user to
                        accept again.
                    </p>
                    <label className="block">
                        <span className="text-xs font-medium text-gray-600">Title</span>
                        <input
                            value={title}
                            maxLength={200}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="e.g. eMedHub Referral Programme — Terms of Participation"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                                focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </label>
                    <div>
                        <span className="text-xs font-medium text-gray-600">Terms text</span>
                        <div className="mt-1">
                            <RichTextEditor
                                value={body}
                                onChange={setBody}
                                height={280}
                                placeholder="Use headings for sections and a numbered list for clauses."
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                            {active
                                ? `This becomes version ${active.version + 1} and resets ${active.accepted_count} acceptance(s).`
                                : "This becomes version 1."}
                        </span>
                        <button
                            type="button"
                            disabled={!canPublish}
                            onClick={() => setConfirming(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm
                                font-medium text-white bg-blue-600 hover:bg-blue-700
                                disabled:opacity-50"
                        >
                            {publishing && <Loader2 className="w-4 h-4 animate-spin" />}
                            {publishing ? "Publishing…" : "Publish version"}
                        </button>
                    </div>
                </div>
            </section>

            {/* ── History ─────────────────────────────────────────────── */}
            <section className="rounded-lg border border-gray-200 bg-white">
                <div className="px-4 py-3 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-900">Published versions</h2>
                </div>

                {loadingTerms && (
                    <div className="flex items-center justify-center py-10 text-gray-400">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                    </div>
                )}

                {!loadingTerms && versions.length === 0 && (
                    <p className="px-4 py-8 text-center text-sm text-gray-500">
                        Nothing published yet.
                    </p>
                )}

                <div className="divide-y divide-gray-100">
                    {!loadingTerms && versions.map(v => (
                        <div key={v.version}>
                            <button
                                type="button"
                                onClick={() => setOpenVersion(openVersion === v.version ? null : v.version)}
                                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50"
                            >
                                <span className="text-sm font-medium text-gray-900">v{v.version}</span>
                                {v.is_active && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full
                                        text-xs font-medium border bg-green-100 text-green-800 border-green-200">
                                        Live
                                    </span>
                                )}
                                <span className="text-sm text-gray-700 flex-1 truncate">{v.title}</span>
                                <span className="text-xs text-gray-500 shrink-0">
                                    {v.accepted_count} accepted
                                </span>
                                {/* Whether eMedHub has it. A document nobody can see is
                                    indistinguishable from an unpublished one. */}
                                {v.pushed_at ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                                ) : (
                                    <span title={v.push_error || "Not yet delivered to eMedHub"}>
                                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                                    </span>
                                )}
                                <ChevronDown className={cn(
                                    "w-4 h-4 text-gray-400 transition-transform shrink-0",
                                    openVersion === v.version && "rotate-180",
                                )} />
                            </button>

                            {openVersion === v.version && (
                                <div className="px-4 pb-4 space-y-2">
                                    <div className="text-xs text-gray-500">
                                        Published {formatDate(v.published_at)}
                                        {v.published_by ? ` by ${v.published_by}` : ""}
                                        {v.push_error && (
                                            <span className="text-amber-700">
                                                {" "}· delivery problem: {v.push_error}
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm
                                            text-gray-800 [&_h2]:font-semibold [&_h2]:mt-2
                                            [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-6
                                            [&_ul]:list-disc [&_ul]:pl-6 [&_p]:my-1"
                                        // Sanitised server-side on write by
                                        // platform_core.releases.sanitize, the same path
                                        // release notes take.
                                        dangerouslySetInnerHTML={{ __html: v.body }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {confirming && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
                        <h3 className="text-base font-semibold text-gray-900">
                            {active ? `Publish version ${active.version + 1}?` : "Publish version 1?"}
                        </h3>
                        <p className="mt-2 text-sm text-gray-600">
                            {active
                                ? `Everyone who accepted version ${active.version} will be asked again, and cannot submit or view referrals until they do. This cannot be undone.`
                                : "Hospital users will be asked to accept these before they can refer."}
                        </p>
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setConfirming(false)}
                                className="px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={publish}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-white
                                    bg-blue-600 hover:bg-blue-700"
                            >
                                Publish
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
